import React, { useState, useEffect, Fragment } from "react";
import { fetchJson } from "../../lib/Fetching";
import styled, { css } from "styled-components";
import Link from "../Link";
import SamplesResults from "../sample_testing/SamplesResults";
import MyGraph from "./trending/MyGraph";
import { paramsDate } from "../../lib/DateFormatting";
import moment from "moment";
import Button from "../Button";

const SelectType = styled.div.attrs({ className: "" })`
  text-align: left;
`;

const NoWrap = styled.td`
  white-space: nowrap;
`;

const ResultsWrapper = styled.div`
  max-width: 1600px;
  padding: 10px;
  margin: 0 auto;
`;

const ResultTable = styled.table`
  font-size: 10pt;
  margin-bottom: 0px;
  td {
    padding: 3px !important;
  }
`;

const ResultRow = styled.tr`
  ${(props) =>
    props.failed
      ? css`
          background-color: #ffe3e3;
        `
      : props.warning
        ? css`
            background-color: #ffeecf;
          `
        : ""};
`;

const LimitSpan = styled.span`
  margin-right: 10px;
  ${(props) =>
    props.type == "pass_fail" &&
    css`
      color: red;
    `} ${(props) =>
    props.type == "warning" &&
    css`
      color: orange;
    `};
`;

const ResultLink = styled(Link)`
  ${(props) =>
    props.active &&
    css`
      color: green;
      font-weight: bold;
    `};
`;

export default function SampleResults({
  sample,
  onClose,
  clientView = true,
  searchType,
  excludeTestNameRegex,
}) {
  const [sampleTestsAndTests, setSampleTestsAndTests] = useState([]);
  const sampleTests = sampleTestsAndTests[0] || [];
  const tests = sampleTestsAndTests[1] || [];
  const [graphTest, setGraphTest] = useState(null);

  const [showResults, setShowResults] = useState(false);
  const [resultsToShow, setResultsToShow] = useState(false);
  const [mode, setMode] = useState("");

  const findTests = ({ exceed, with_results, found }) =>
    fetchJson("/sample_testing/sample_tests.json", {
      form: {
        sample_id: sample.identifier,
        with_results,
        exceed,
        found,
      },
    }).then((json) => {
      if (json.samples.length == 0) {
        setSampleTestsAndTests([]);
      } else {
        const sampleTests = json.samples[0].sample_tests;
        setSampleTestsAndTests([sampleTests, json.tests]);
      }
      setShowResults(true);
    });

  const findExceededResults = () => findTests({ exceed: true }).then((_) => setMode("exceed"));
  const findAllResults = () => findTests({}).then((_) => setMode("all"));
  const findDetectedResults = () => findTests({ found: true }).then((_) => setMode("found"));
  const findAvailableResults = () =>
    findTests({ with_results: true }).then((_) => setMode("available"));
  const findLabResults = () => setMode("lab");

  useEffect(
    () => {
      setShowResults(false);
      if (searchType === "exceeded_results") findExceededResults();
      if (searchType === "all_results") findAllResults();
      if (searchType === "detected_results") findDetectedResults();
      if (searchType === "available_results") findAvailableResults();
      if (searchType === "laboratory_results") setMode("lab");
    },
    [searchType]
  );

  // Find all limit types for the given tests
  const limitTypes = [];
  sampleTests.map((sampleTest) => {
    sampleTest.limit_types &&
      sampleTest.limit_types.map((limitType) => {
        if (!limitTypes.includes(limitType.type)) {
          limitTypes.push(limitType.type);
        }
      });
  });

  const mrlsShown = limitTypes && limitTypes.length > 0 && limitTypes[0].match(/MRL/);

  const getTest = (test_id) => tests[test_id];

  const renderTable = () => {
    if (!["all", "exceed", "found", "available"].includes(mode)) return null;

    var chart;
    if (graphTest)
      chart = {
        id: "product_" + sample.product_id + "_compound_" + graphTest.testname_id,
        label: "Graph",
        full_description:
          sample.product_name + " / " + graphTest.test_name + " (last 6 months data)",
        product_ids: [sample.product_id],
        test_name_ids: [graphTest.testname_id],
        start_date: paramsDate(moment().subtract(6, "months")),
        end_date: paramsDate(moment()),
      };

    const displaySampleTests = excludeTestNameRegex
      ? sampleTests.filter((sampleTest) => {
          const test = tests[sampleTest.test_id];
          return !test.test_name.match(excludeTestNameRegex);
        })
      : sampleTests;
    return (
      <Fragment>
        {graphTest && (
          <div>
            <MyGraph chart={chart} removeChart={() => setGraphTest(null)} />
          </div>
        )}
        {mrlsShown && <p>Any MRLs shown are EU MRLs</p>}
        <ResultTable className="table table-condensed">
          <thead>
            <tr>
              <th>{I18n.t("test_name")}</th>
              <th>{I18n.t("technique")}</th>
              <th>{I18n.t("accreditation_short")}</th>
              <th>{I18n.t("lod")}</th>
              <th>{I18n.t("result")}</th>
              {limitTypes.map((limitType) => (
                <th key={limitType}>{limitType}</th>
              ))}
              <th>{I18n.t("reviewed?")}</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {displaySampleTests.map((sampleTest) => {
              const test = tests[sampleTest.test_id];

              return !sampleTest.test_result_id ? (
                <ResultRow failed={false} warning={false} key={test.test_id}>
                  <td>{test.test_name}</td>
                  <NoWrap>{test.technique_name}</NoWrap>
                  <NoWrap>{test.accreditation_name}</NoWrap>
                  <td colSpan={4 + limitTypes.length}>Awaiting result...</td>
                </ResultRow>
              ) : (
                <ResultRow
                  failed={sampleTest.failed}
                  warning={sampleTest.warning}
                  key={sampleTest.test_result_id}
                >
                  <td>{test.test_name}</td>
                  <NoWrap>{test.technique_name}</NoWrap>
                  <NoWrap>{test.accreditation_name}</NoWrap>
                  <NoWrap>{test.lod_with_units}</NoWrap>
                  <NoWrap>{sampleTest.formatted_result}</NoWrap>
                  {limitTypes.map((limitType) => (
                    <NoWrap key={limitType}>
                      {sampleTest.limit_types &&
                        sampleTest.limit_types.filter((lt) => lt.type == limitType).map((lt) =>
                          lt.limits.map((limit, i) => (
                            <LimitSpan key={i} type={limit.type}>
                              {limit.formatted}
                            </LimitSpan>
                          ))
                        )}
                    </NoWrap>
                  ))}
                  <td>
                    {sampleTest.reviewed ? (
                      <i className="fa fa-check" />
                    ) : (
                      <i className="fa fa-times" />
                    )}
                  </td>
                  <td>
                    <Button tiny icon="fa-line-chart" onClick={() => setGraphTest(test)} />
                  </td>
                </ResultRow>
              );
            })}
          </tbody>
        </ResultTable>
      </Fragment>
    );
  };

  return (
    <ResultsWrapper>
      {showResults && renderTable()}
      {showResults &&
        mode !== "lab" &&
        sampleTests.length == 0 && <p>{I18n.t("there_are_no_results_yet")}</p>}
      {!clientView &&
        mode == "lab" && (
          <SamplesResults
            searchParams={{ sample_id: sample.identifier }}
            initialSimpleSampleView={true}
            clientView={clientView}
          />
        )}
    </ResultsWrapper>
  );
}
